import React, { useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faCircleInfo, faCheckCircle, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Device } from '../types';

type AlertType = 'info' | 'success' | 'failure';

interface DeviceAlertModalProps {
  apiBaseUrl: string;
  device: Device | null;
  isOpen: boolean;
  onClose: () => void;
}

const alertOptions: Array<{
  type: AlertType;
  label: string;
  description: string;
  buttonClass: string;
  icon: typeof faBolt;
}> = [
  {
    type: 'info',
    label: 'Info Alert',
    description: 'Trigger the blue information alert.',
    buttonClass: 'btn-outline-primary',
    icon: faCircleInfo,
  },
  {
    type: 'success',
    label: 'Success Alert',
    description: 'Trigger the green success alert.',
    buttonClass: 'btn-outline-success',
    icon: faCheckCircle,
  },
  {
    type: 'failure',
    label: 'Failure Alert',
    description: 'Trigger the red failure alert.',
    buttonClass: 'btn-outline-danger',
    icon: faTriangleExclamation,
  },
];

const DeviceAlertModal: React.FC<DeviceAlertModalProps> = ({
  apiBaseUrl,
  device,
  isOpen,
  onClose,
}) => {
  const [submittingAlert, setSubmittingAlert] = useState<AlertType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTriggerAlert = async (alertType: AlertType) => {
    if (!device) {
      return;
    }

    setSubmittingAlert(alertType);
    setError(null);

    try {
      await axios.post(
        `${apiBaseUrl}/devices/${device.serial}/alerts/${alertType}`,
        {},
        { withCredentials: true }
      );
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to trigger alert');
    } finally {
      setSubmittingAlert(null);
    }
  };

  if (!isOpen || !device) {
    return null;
  }

  return (
    <>
      <div className="modal-backdrop fade show" onClick={submittingAlert ? undefined : onClose}></div>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1} role="dialog">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <FontAwesomeIcon icon={faBolt} className="me-2 text-warning" />
                Trigger Device Alert
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                disabled={submittingAlert !== null}
              ></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning">
                <strong>Warning:</strong> This sends a real alert command to{' '}
                <strong>{device.serial}</strong> and could affect a customer's device in production.
              </div>

              <div className="mb-3">
                <div className="small text-muted mb-1">Device</div>
                <div>
                  <strong>{device.model}</strong> running {device.firmware}
                </div>
              </div>

              {error && <div className="alert alert-danger">{error}</div>}

              <div className="d-grid gap-2">
                {alertOptions.map((option) => (
                  <button
                    key={option.type}
                    type="button"
                    className={`btn ${option.buttonClass} text-start`}
                    onClick={() => handleTriggerAlert(option.type)}
                    disabled={submittingAlert !== null}
                  >
                    {submittingAlert === option.type ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    ) : (
                      <FontAwesomeIcon icon={option.icon} className="me-2" />
                    )}
                    <strong>{option.label}</strong>
                    <span className="d-block small ms-4">{option.description}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={submittingAlert !== null}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeviceAlertModal;
